import { AfterViewInit, Component, ElementRef, EventEmitter, inject, OnInit, Output, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IconDefinition } from '@ant-design/icons-angular';
import { AppstoreAddOutline, PlusOutline } from '@ant-design/icons-angular/icons';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule, NzIconService } from 'ng-zorro-antd/icon';
import { NzInputDirective, NzInputGroupComponent } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NZ_MODAL_DATA, NzModalFooterDirective, NzModalRef, NzModalService, NzModalTitleDirective } from 'ng-zorro-antd/modal';
import { NzTableCellDirective, NzTableComponent, NzTableModule, NzTbodyComponent, NzTheadComponent, NzThMeasureDirective, NzTrDirective } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TITLE_TABLE_SIZE } from 'src/app/core/constants/style';
import { FinalProductService } from 'src/app/private/services/final-product.service';
import { PackagingsService } from 'src/app/private/services/packagings.service';
import { FinalProductDto } from 'src/app/shared/dto/final-product.dto';
import { PackagingDto } from 'src/app/shared/dto/packaging.dto';
import { SourceEnum } from 'src/app/shared/enums/source.enum';
import { isMmlProcessName, isUdProcessName, getDisplayUnit } from 'src/app/shared/utils/utils';
import { ModalFromToComponent } from '../modal-from-to/modal-from-to.component';
import { ModalRawMaterialsFinalProductOrPackagingComponent } from '../modal-raw-materials-final-product-or-packaging/modal-raw-materials-final-product-or-packaging.component';
import { ModalSelectPackagingTypeComponent } from '../modal-select-packaging-type/modal-select-packaging-type.component';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { LoaderComponent } from 'src/app/shared/components/loader/loader.component';
import _ from 'lodash';

const icons: IconDefinition[] = [
    PlusOutline,
    AppstoreAddOutline
];

@Component({
    selector: 'app-modal-packagings-by-final-product',
    standalone: true,
    imports: [
        NzModalTitleDirective,
        NzModalFooterDirective,
        NzTableModule,
        NzTagModule,
        FormsModule,
        ReactiveFormsModule,
        NzTableComponent,
        NzTheadComponent,
        NzTrDirective,
        NzTableCellDirective,
        NzThMeasureDirective,
        NzTbodyComponent,
        NzMessageModule,
        NzInputDirective,
        NzEmptyModule,
        NzInputGroupComponent,
        NzIconModule,
        NzToolTipModule,
        NzDividerComponent,
        LoaderComponent,
        NzButtonModule
    ],
    templateUrl: './modal-packagings-by-final-product.component.html',
    styleUrl: './modal-packagings-by-final-product.component.scss'
})
export class ModalPackagingsByFinalProductComponent implements OnInit, AfterViewInit {
    @Output() emitService = new EventEmitter();
    @ViewChild('modalContent') private readonly _modalContent!: ElementRef;
    @ViewChild('basicTable', { static: false }) nzTableComponent?: NzTableComponent<PackagingDto>;

    isLoading: boolean = true;
    isSaving: boolean = false;
    finalProduct: FinalProductDto = new FinalProductDto();
    packagings: PackagingDto[] = [];
    orderProcessId?: number;
    productionProcessName!: string;
    tableHeight!: number;

    readonly data = inject(NZ_MODAL_DATA);
    private readonly message = inject(NzMessageService);
    private readonly modalService = inject(NzModalService);
    private readonly modalRef = inject(NzModalRef);
    private readonly packagingsService = inject(PackagingsService);
    private readonly finalProductsService = inject(FinalProductService);
    private readonly iconService = inject(NzIconService);

    /**
     * Devuelve la unidad de medida a mostrar según el proceso de producción
     */
    getDisplayUnit(): string {
        return getDisplayUnit(undefined, this.productionProcessName, this.finalProduct?.article?.measurementUnit?.abbreviation || '');
    }

    isKgProcess(): boolean {
        return isMmlProcessName(this.productionProcessName);
    }

    ngOnInit(): void {
        this.iconService.addIcon(...icons);

        this.orderProcessId = this.data?.orderProcessId;
        this.productionProcessName = this.data?.productionProcessName;

        if (!this.productionProcessName) {
            this.isLoading = false;
            this.message.error('No se ha proporcionado el nombre del proceso de producción');
            this.modalRef.close();
            return;
        }

        this.finalProduct = this.data?.finalProduct;

        if (!this.finalProduct) {
            this.isLoading = false;
            this.message.error('No se han encontrado productos finales');
            return;
        }

        /*
            ToDo:
                - Connect to packagings websocket to refresh them if there are changes (i.e. from sensors)
                - When the modal is going to be closed, disconnect from the socket room
        */
        this.packagings = this.data?.packagings;

        if (this.isKgProcess()) {
            this.packagings = (this.packagings ?? []).map((packaging) => ({
                ...packaging,
                axuliaryQuantity: packaging.axuliaryQuantity ?? 0
            }));
        }

        if (!this.finalProduct.packagings) {
            this.isLoading = false;
            this.message.error('No se han encontrado formatos');
            return;
        }

        // this.fillQuanityPackagings();

        this.isLoading = false;
    }

    async ngAfterViewInit(): Promise<void> {
        setTimeout(() => {
            this.tableHeight =
                (this._modalContent.nativeElement as HTMLImageElement)
                    .clientHeight - TITLE_TABLE_SIZE;
        });
    }

    async onLinkRawMaterials(packaging: PackagingDto) {
        const modal = this.modalService.create({
            nzContent: ModalRawMaterialsFinalProductOrPackagingComponent,
            nzStyle: { width: '80%', top: '25%' },
            nzBodyStyle: { height: '50vh' },
            nzClosable: false,
            nzFooter: null,
            nzData: {
                nzTitle: `Materias primas de ${this.finalProduct.article?.name} del formato: ${packaging.packagingType?.name} - ${packaging.batchNumber}`,
                orderProcessId: this.orderProcessId,
                packaging: packaging,
                finalProduct: this.finalProduct,
                source: SourceEnum.PACKAGING
            },
        });

        modal.componentInstance?.emitService.subscribe(async updatedRawMaterials => {
            this.packagings.find(p => p.id === packaging.id)!.rawMaterials = updatedRawMaterials;
        });
    }

    async onLinkMultipleRawMaterials() {
        const modalSelectWithInputs = await this.modalService.create({
            nzContent: ModalFromToComponent,
            nzStyle: { width: '50%', top: '20%' },
            nzBodyStyle: { height: '30vh' },
            nzClosable: false,
            nzFooter: null,
            nzData: {
                nzTitle: `Indique de que formato a que formato desea añadir las materias primas`,
                max: this.packagings.length
            },
        });

        modalSelectWithInputs.componentInstance?.emitService.subscribe(async (response: { from: number, to: number }) => {
            const from: number = response.from;
            const to: number = response.to;
            const packagingsToAddRawMaterials: number[] = this.packagings.slice(from - 1, to).map(packaging => packaging.id!);

            const modal = this.modalService.create({
                nzContent: ModalRawMaterialsFinalProductOrPackagingComponent,
                nzStyle: { width: '90%', top: '15%' },
                nzBodyStyle: { height: '50vh' },
                nzClosable: false,
                nzFooter: null,
                nzData: {
                    nzTitle: `Añadir materias primas del formato ${from} al formato ${to}`,
                    orderProcessId: this.orderProcessId,
                    packagingsToAddRawMaterialsIds: packagingsToAddRawMaterials,
                    finalProduct: this.finalProduct,
                    source: SourceEnum.PACKAGING_MULTIPLE
                }
            });
            modal.componentInstance?.emitService.subscribe(async (finalProducts: FinalProductDto[]) => {
                const finalProduct: FinalProductDto | undefined = finalProducts.find(fp => fp.id! === this.finalProduct.id!);

                if (finalProduct) {
                    this.packagings = _.cloneDeep(finalProduct?.packagings!);
                } else {
                    this.message.error(`Algo ha ido mal al asignar las materias primas a los formatos desde ${from} al ${to} del producto ${this.finalProduct.id}`);
                }
            });
        });
    }

    addPackaging() {
        const modal = this.modalService.create({
            nzContent: ModalSelectPackagingTypeComponent,
            nzStyle: { width: '40%', top: '15%' },
            nzBodyStyle: { height: 'auto' },
            nzClosable: false,
            nzFooter: null,
            nzData: {
                nzTitle: `Añadir formato al producto final: ${this.finalProduct.article?.name}`,
                finalProduct: this.finalProduct
            }
        });

        modal.afterClose.subscribe(async (packaging: PackagingDto) => {
            if (packaging) {
                this.packagings = [...this.packagings, packaging];
            }
        });
    }

    async removePackaging(packagingToRemove: PackagingDto) {
        try {
            if(!(await this.confirmRemovePackaging())){
                return;
            };
            this.isLoading = true;
            this.packagings = this.packagings.filter(p => p.id !== packagingToRemove.id);
            await this.finalProductsService.removePackagingFromFinalProduct(this.finalProduct.id!, packagingToRemove.id!);
            this.message.success('Formato eliminado correctamente');
            this.isLoading = false;
        } catch (error) {
            this.message.error('No se ha podido eliminar el formato');
            this.isLoading = false;
        }
    }

    async handleSave() {
        try {
            //TODO: if this modal remains create a function to update all packages in one go
            // for (const packaging of this.packagings) {
            //     await this.packagingsService.updatePackagingQuantity(packaging.id!, packaging.quantity!);
            // }

            this.isSaving = true;

            const packagingsToUpdate: PackagingDto[] = this.packagings.map(p => {
                return {
                    id: p.id!,
                    quantity: p.quantity!,
                    ...(this.isKgProcess() && { axuliaryQuantity: p.axuliaryQuantity }),
                    packagingTypeId: p.packagingTypeId,
                    articleId: p.articleId
                };
            });

            await this.packagingsService.updatePackagingsQuantity(packagingsToUpdate);

            this.message.success('Cantidades de formatos actualizados correctamente');

            this.isSaving = false;
            this.emitService.next(this.packagings);
            this.modalRef.close();
        } catch (e: any) {
            this.isSaving = false;
            this.message.error(e.message);
        }
    }

    handleCancel() {
        this.emitService.next(this.packagings);
        this.modalRef.close();
    }

    trackByIndex(_: number, data: PackagingDto): number {
        return data.id!;
    }

    private async confirmRemovePackaging(): Promise<boolean> {
        return new Promise((resolve) => {
            this.modalService.confirm({
                nzTitle: '<i>¿Seguro que quieres eliminar el formato?</i>',
                nzContent: '<b>Una vez eliminado no podrar crear otro con el mismo número de lote.</b>',
                nzClosable: false,
                nzOkText: 'Eliminar formato',
                nzOnOk: () => { resolve(true); },
                nzCancelText: 'Cancelar',
                nzOnCancel: () => { resolve(false); }
            });
        });
    }
}
